// src/components/aiinsight/InsightCard.jsx
// Renders a single AI response: narrative summary + chart + raw data drill-down

import React, { useState } from 'react';
import {
    Box, Card, CardContent, Chip, Collapse, Divider, IconButton,
    Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AiChartRenderer from './AiChartRenderer';
import InsightSummary from './InsightSummary';

// ── Markdown cleaner ──────────────────────────────────────────────────────────
// Cleans LLM spacing bugs (especially GPT-5's ** text ** pattern) line-by-line
// before passing to ReactMarkdown. Preserves headings, lists, blockquotes.
const cleanMarkdown = (text) => {
    if (!text) return text;

    const fixLine = (line) => line
        // GPT-5 frequently outputs "** text **" (with padding spaces inside **)
        // This is invalid markdown — parsers see it as literal asterisks.
        // Fix: trim the inner padding so "** text **" → "**text**"
        .replace(/\*\*\s+([^*]+?)\s+\*\*/g, '**$1**')   // "** word **" → "**word**"
        .replace(/\*\*\s+([^*]+?)\*\*/g,    '**$1**')   // "** word**"  → "**word**"
        .replace(/\*\*([^*]+?)\s+\*\*/g,    '**$1**')   // "**word **"  → "**word**"

        // Same for underscored bold
        .replace(/__\s+([^_]+?)\s+__/g, '__$1__')
        .replace(/__\s+([^_]+?)__/g,    '__$1__')
        .replace(/__([^_]+?)\s+__/g,    '__$1__')

        // Non-standard bullet symbols → standard markdown
        .replace(/^[\u25CF\u2022]\s+/, '- ')

        // Strip leaking XML sentiment tags (from old prompts)
        .replace(/<\/?(?:good|bad|b)\b[^>]*>/ig, '');

    return text
        .split(/\r?\n/)
        .map(fixLine)
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const parseNarrative = (text) => {
    if (!text) return null;
    const parts = text.split(/(<good>.*?<\/good>|<bad>.*?<\/bad>|<b>.*?<\/b>)/gis);
    return parts.map((part, i) => {
        const lowerPart = part.toLowerCase();
        if (lowerPart.startsWith('<good>')) {
            const content = part.replace(/<\/?good>/gi, '');
            return (
                <span key={i} style={{ color: '#22c55e', fontWeight: 600 }}>{content}</span>
            );
        }
        if (lowerPart.startsWith('<bad>')) {
            const content = part.replace(/<\/?bad>/gi, '');
            return (
                <span key={i} style={{ color: '#f43f5e', fontWeight: 600 }}>{content}</span>
            );
        }
        if (lowerPart.startsWith('<b>')) {
            const content = part.replace(/<\/?b>/gi, '');
            return <strong key={i}>{content}</strong>;
        }
        return <span key={i}>{part}</span>;
    });
};

// ── Operational Story Bullets ────────────────────────────────────────────────
// Renders AI narrative as a traditional "AI Operational Story" bullet list.
// Each sentence becomes a bullet with a context-aware icon:
//   <good> → green check circle  ✓
//   <bad>  → red alert circle    !
//   other  → blue info/lightbulb ●
const NarrativeBullets = ({ text, sx = {} }) => {
    if (!text) return null;

    // Detect overall sentiment of a raw bullet string (before stripping tags)
    const detectSentiment = (rawBullet) => {
        if (/<good>/i.test(rawBullet)) return 'good';
        if (/<bad>/i.test(rawBullet)) return 'bad';
        // Keyword heuristics for sentences without explicit tags
        const lower = rawBullet.toLowerCase();
        if (/\b(grow|increas|improv|exceed|above|positive|rise|gain|higher|opportunit)\b/.test(lower)) return 'good';
        if (/\b(declin|drop|fall|concern|below|negative|loss|reduc|cancel|risk|warn|alert)\b/.test(lower)) return 'bad';
        if (/\b(recommend|action|suggest|ensure|consider|should|opportunity)\b/.test(lower)) return 'insight';
        if (/\b(trend|track|monitor|analyz|pattern|correlat)\b/.test(lower)) return 'trend';
        return 'neutral';
    };

    const sentimentStyle = {
        good: { icon: '✓', color: '#10b981', bg: 'rgba(16,185,129,0.06)', border: '#10b981' },
        bad: { icon: '!', color: '#f43f5e', bg: 'rgba(244,63,94,0.06)', border: '#f43f5e' },
        insight: { icon: '💡', color: '#f59e0b', bg: 'rgba(245,158,11,0.06)', border: '#f59e0b' },
        trend: { icon: '↗', color: '#818cf8', bg: 'rgba(129,140,248,0.06)', border: '#818cf8' },
        neutral: { icon: '●', color: '#6366f1', bg: 'rgba(99,102,241,0.04)', border: 'rgba(99,102,241,0.25)' },
    };

    // Strip tags for splitting only
    const clean = text.replace(/<\/?(?:good|bad|b)>/gi, '');
    const rawLines = clean.split(/\n+/);
    const bullets = [];

    // Also keep original-text fragments to detect tags per bullet
    let searchPos = 0;
    rawLines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const sentences = trimmed.split(/(?<=[.!?])\s+/);
        sentences.forEach(s => {
            const t = s.trim();
            if (t.length > 3) {
                // Find the raw fragment with tags in original text
                const start = text.indexOf(t.slice(0, 20), searchPos);
                const rawFrag = start >= 0 ? text.slice(start, start + t.length + 20) : t;
                searchPos = start >= 0 ? start + 1 : searchPos;
                bullets.push({ clean: t, raw: rawFrag });
            }
        });
    });

    if (bullets.length === 0) return null;

    return (
        <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
            {bullets.map(({ clean: bullet, raw }, i) => {
                const sentiment = detectSentiment(raw);
                const { icon, color, bg, border } = sentimentStyle[sentiment];
                return (
                    <Box
                        key={i}
                        component="li"
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.25,
                            px: 1.5,
                            py: 0.9,
                            borderRadius: 1.5,
                            bgcolor: bg,
                            borderLeft: `3px solid ${border}`,
                            transition: 'background 0.15s',
                            '&:hover': { bgcolor: `${bg}`, filter: 'brightness(0.97)' },
                        }}
                    >
                        {/* Sentiment icon badge */}
                        <Box
                            component="span"
                            sx={{
                                flexShrink: 0,
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                border: `1.5px solid ${color}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: sentiment === 'insight' ? 11 : 10,
                                fontWeight: 700,
                                color,
                                mt: '1px',
                                lineHeight: 1,
                                bgcolor: `${color}18`,
                            }}
                        >
                            {icon}
                        </Box>
                        {/* Bullet text with inline tag rendering + number highlighting */}
                        <Box component="span" sx={{ fontSize: 11.5, lineHeight: 1.6, color: 'text.primary', flex: 1 }}>
                            {parseNarrativeWithNumbers(bullet)}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

// parseNarrativeWithNumbers: extends parseNarrative to also highlight numbers/percentages in amber
const parseNarrativeWithNumbers = (text) => {
    if (!text) return null;
    // First split on XML tags (good/bad/b), then within plain segments highlight numbers
    const xmlParts = text.split(/(<good>.*?<\/good>|<bad>.*?<\/bad>|<b>.*?<\/b>)/gis);
    const result = [];
    xmlParts.forEach((part, pi) => {
        const lower = part.toLowerCase();
        if (lower.startsWith('<good>')) {
            const c = part.replace(/<\/?good>/gi, '');
            result.push(<span key={`g${pi}`} style={{ color: '#10b981', fontWeight: 600 }}>{c}</span>);
        } else if (lower.startsWith('<bad>')) {
            const c = part.replace(/<\/?bad>/gi, '');
            result.push(<span key={`b${pi}`} style={{ color: '#f43f5e', fontWeight: 600 }}>{c}</span>);
        } else if (lower.startsWith('<b>')) {
            const c = part.replace(/<\/?b>/gi, '');
            result.push(<strong key={`s${pi}`}>{c}</strong>);
        } else {
            // Highlight numbers, percentages, currency — but NOT years (1900-2099) or ID-like integers
            const yearPattern = /^(19|20)\d{2}(-\d{2})?(-\d{2})?$/;
            // Skip bare integers ≥10000 with no $, comma or % — likely IDs/keys
            const idLikePattern = /^\d{5,}$/;
            const numParts = part.split(/(\$?[\d,]+\.?\d*%?|\d+\.\d+%?)/g);
            numParts.forEach((seg, si) => {
                const clean = seg.replace(/,/g, '');
                if (
                    /^(\$?[\d,]+\.?\d*%?|\d+\.\d+%?)$/.test(seg) &&
                    seg.length > 0 &&
                    !yearPattern.test(clean) &&
                    !idLikePattern.test(clean)
                ) {
                    result.push(
                        <span key={`n${pi}_${si}`} style={{ color: '#f59e0b', fontWeight: 700 }}>
                            {seg}
                        </span>
                    );
                } else {
                    result.push(<span key={`t${pi}_${si}`}>{seg}</span>);
                }
            });
        }
    });
    return result;
};


// ── Format numeric values to 2 decimal places ─────────────────────────────────────
const formatNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return value;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num % 1 === 0 ? num : num.toFixed(2);
};

// ── DataGrid drill-down ───────────────────────────────────────────────────────
const DataTablePanel = ({ data }) => {
    if (!data || data.length === 0) return null;

    const columns = Object.keys(data[0]).map((key) => ({
        field: key,
        headerName: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        flex: 1,
        minWidth: 100,
        valueFormatter: (value) => formatNumericValue(value),
    }));

    const rows = data.map((row, idx) => ({ id: idx, ...row }));

    return (
        <Box sx={{ mt: 1, height: 260 }}>
            <DataGrid
                rows={rows}
                columns={columns}
                density="compact"
                disableRowSelectionOnClick
                pageSizeOptions={[5, 10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                sx={{
                    border: '1px solid rgba(99,102,241,0.2)',
                    borderRadius: 2,
                    fontSize: 12,
                    '& .MuiDataGrid-columnHeaders': { background: 'rgba(99,102,241,0.1)' },
                    '& .MuiDataGrid-cell': { borderColor: 'rgba(255,255,255,0.05)' },
                }}
            />
        </Box>
    );
};

// ── InsightCard ───────────────────────────────────────────────────────────────
const InsightCard = ({ query, onAddToReport, onFollowUp }) => {
    const [showData, setShowData] = useState(false);
    const [showSQL, setShowSQL] = useState(false);

    const hasChart = query.viz_config_json && (query.intent === 'visualize' || query.intent === 'compare');
    // Handle both old format (direct array) and new format (with metadata)
    const actualData = query.result_json?.data || query.result_json;
    const hasData = actualData && actualData.length > 0;

    const intentColors = {
        visualize: 'primary',
        summarize: 'secondary',
        answer: 'default',
    };

    return (
        <Card
            sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 32px rgba(99,102,241,0.2)' : '0 6px 32px rgba(99,102,241,0.1)' },
            }}
        >
            {/* Top strip */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(90deg, rgba(99,102,241,0.18) 0%, transparent 100%)' : 'linear-gradient(90deg, rgba(99,102,241,0.06) 0%, transparent 100%)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <AutoAwesomeIcon sx={{ fontSize: 16, color: '#818cf8' }} />
                <Typography variant="caption" sx={{ color: '#818cf8', fontWeight: 700, letterSpacing: 0.5 }}>
                    AI INSIGHT
                </Typography>
                {query.intent && (
                    <Chip
                        label={query.intent}
                        size="small"
                        color={intentColors[query.intent] || 'default'}
                        sx={{ ml: 1, height: 18, fontSize: 10 }}
                    />
                )}
                {/* Time filter indicator */}
                {query.result_json?.has_time_filter && query.result_json?.time_filter && (
                    <Chip
                        icon={<span>📅</span>}
                        label={query.result_json.time_filter.label}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: 10, borderColor: 'rgba(99,102,241,0.5)', color: '#6366f1' }}
                    />
                )}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {onAddToReport && (
                        <Tooltip title="Add to Report Builder">
                            <IconButton size="small" onClick={() => onAddToReport(query)} sx={{ color: 'text.secondary' }}>
                                <DescriptionIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {query.token_usage && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                            {query.token_usage} tokens
                        </Typography>
                    )}
                    {query.generated_sql && (
                        <Tooltip title="View generated SQL">
                            <IconButton size="small" onClick={() => setShowSQL((p) => !p)} sx={{ color: 'text.secondary' }}>
                                <CodeIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <CardContent sx={{ p: 2, pb: '12px !important' }}>
                {/* SQL code block */}
                <Collapse in={showSQL}>
                    <Box
                        component="pre"
                        sx={{
                            background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.04)',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 1.5,
                            fontSize: 11,
                            color: 'text.secondary',
                            overflowX: 'auto',
                            mb: 1.5,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {query.generated_sql}
                    </Box>
                </Collapse>

                {/* Narrative summary — auto-detects prose vs bullet format from backend */}
                {query.summary_text && (() => {
                    // Clean raw markdown symbols before processing
                    const cleaned = cleanMarkdown(query.summary_text);

                    // Detect bullet format: original had '- ' or '●' lines
                    const isBullets = query.summary_text
                        .split('\n')
                        .some(line => line.trimStart().startsWith('- ') || line.trimStart().startsWith('● ') || /^●/.test(line.trimStart()));

                    // Use InsightSummary for proper Markdown rendering
                    return <InsightSummary text={cleaned} sx={{ mb: hasChart ? 2 : 0 }} />;
                })()}

                {/* Chart */}
                {hasChart && (
                    <>
                        {query.viz_config_json?.title && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                {query.viz_config_json.title}
                            </Typography>
                        )}
                        <AiChartRenderer vizConfig={query.viz_config_json} data={actualData} />
                    </>
                )}

                {/* Raw data expand */}
                {hasData && (
                    <>
                        <Divider sx={{ my: 1.5, borderColor: 'divider' }} />
                        <Box
                            onClick={() => setShowData((p) => !p)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                                transition: 'color 0.15s',
                            }}
                        >
                            {showData ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
                                {showData ? 'Hide' : 'View'} Raw Data ({actualData.length} rows)
                            </Typography>
                        </Box>
                        <Collapse in={showData}>
                            <DataTablePanel data={actualData} />
                        </Collapse>
                    </>
                )}

                {/* Error state */}
                {query.status === 'error' && (
                    <Box
                        sx={{
                            mt: 1,
                            p: 1.5,
                            background: 'rgba(244,63,94,0.08)',
                            border: '1px solid rgba(244,63,94,0.2)',
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#f43f5e' }}>
                            ⚠ {query.error_message || 'An error occurred processing this query.'}
                        </Typography>
                    </Box>
                )}

                {/* Follow-up suggestions */}
                {query.followup_questions && query.followup_questions.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1.25, borderColor: 'divider' }} />
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 1 }}>
                            ✨ FOLLOW-UP IDEAS
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {query.followup_questions.map((q, i) => (
                                <Chip
                                    key={i}
                                    label={q}
                                    size="small"
                                    icon={<ArrowForwardIcon sx={{ fontSize: '12px !important' }} />}
                                    onClick={() => onFollowUp?.(q)}
                                    sx={{
                                        fontSize: 11,
                                        cursor: 'pointer',
                                        bgcolor: (theme) => theme.palette.mode === 'dark'
                                            ? 'rgba(99,102,241,0.12)'
                                            : 'rgba(99,102,241,0.07)',
                                        color: 'primary.main',
                                        border: '1px solid',
                                        borderColor: 'rgba(99,102,241,0.25)',
                                        '&:hover': {
                                            bgcolor: 'rgba(99,102,241,0.2)',
                                            borderColor: 'primary.main',
                                        },
                                        transition: 'all 0.15s',
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

            </CardContent>
        </Card>
    );
};

export default InsightCard;
